import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  IconButton, Badge, Menu, MenuItem, Typography, Box, Divider, ListItemIcon,
  Chip, Button, Tooltip,
} from '@mui/material'
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded'
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from 'core'
import { clientApi } from 'services/api'
import { getSocket } from 'services/socket'
import { formatNotification, getOrderStatusMessage } from 'utils/notificationMessages'

const READ_KEY = 'dhanika_notifications_read'
const CLEARED_KEY = 'dhanika_notifications_cleared'

const loadStoredIds = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]')
  } catch {
    return []
  }
}

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

const sortNotifications = (list) =>
  [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

const statusIcon = (type, status) => {
  if (type === 'support_reply') return <SupportAgentRoundedIcon fontSize="small" color="secondary" />
  switch (status) {
    case 'payment_confirmed': return <PaymentsRoundedIcon fontSize="small" color="info" />
    case 'packed':
    case 'ready_for_shipment': return <Inventory2RoundedIcon fontSize="small" color="primary" />
    case 'delivered': return <CheckCircleRoundedIcon fontSize="small" color="success" />
    case 'cancelled':
    case 'returned':
    case 'refunded': return <CancelRoundedIcon fontSize="small" color="error" />
    default: return <LocalShippingRoundedIcon fontSize="small" color="primary" />
  }
}

const NotificationBell = () => {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [anchorEl, setAnchorEl] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [readIds, setReadIds] = useState(() => loadStoredIds(READ_KEY))
  const [clearedIds, setClearedIds] = useState(() => loadStoredIds(CLEARED_KEY))

  const visibleNotifications = useMemo(
    () => notifications
      .filter((n) => !clearedIds.includes(n.id))
      .map(formatNotification),
    [notifications, clearedIds]
  )

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const { data } = await clientApi.notifications.list()
      const items = (data?.data ?? []).map(formatNotification)
      setNotifications(sortNotifications(items))
    } catch {
      setNotifications([])
    }
  }, [isAuthenticated])

  useEffect(() => {
    setAnchorEl(null)
  }, [location.pathname])

  useEffect(() => {
    fetchNotifications()

    // Only poll when the tab is visible — stops hammering the server when minimized
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications()
      }
    }, 120000)

    // Also refresh once when the user comes back to the tab
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchNotifications()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [fetchNotifications])

  useEffect(() => {
    const socket = getSocket()
    if (!socket || !isAuthenticated) return undefined

    const onOrderUpdate = (payload) => {
      const notif = formatNotification({
        id: `live_order_${payload.orderId}_${payload.status}_${Date.now()}`,
        type: 'order_update',
        status: payload.status,
        title: payload.message || getOrderStatusMessage(payload.status),
        message: `Order #${payload.orderNumber}`,
        order_number: payload.orderNumber,
        link: `/orders/${payload.orderId}`,
        created_at: payload.createdAt || new Date().toISOString(),
      })
      setNotifications((prev) => sortNotifications([notif, ...prev]).slice(0, 30))
    }

    socket.on('order_update', onOrderUpdate)
    return () => socket.off('order_update', onOrderUpdate)
  }, [isAuthenticated])

  const unreadCount = visibleNotifications.filter((n) => !readIds.includes(n.id)).length

  const persist = (key, ids) => localStorage.setItem(key, JSON.stringify(ids))

  const markRead = (ids) => {
    const updated = [...new Set([...readIds, ...ids])]
    setReadIds(updated)
    persist(READ_KEY, updated)
  }

  const clearNotifications = (ids) => {
    const updated = [...new Set([...clearedIds, ...ids])]
    setClearedIds(updated)
    persist(CLEARED_KEY, updated)
    markRead(ids)
  }

  const handleOpen = (e) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    setAnchorEl(e.currentTarget)
    fetchNotifications()
  }

  const handleSelect = (notif) => {
    markRead([notif.id])
    setAnchorEl(null)
    if (notif.link) navigate(notif.link)
  }

  const handleClearOne = (e, id) => {
    e.stopPropagation()
    clearNotifications([id])
  }

  const handleMarkAllRead = () => markRead(visibleNotifications.map((n) => n.id))

  const handleClearAll = () => clearNotifications(visibleNotifications.map((n) => n.id))

  return (
    <>
      <Tooltip title={isAuthenticated ? 'Notifications' : 'Sign in to see notifications'}>
        <IconButton
          onClick={handleOpen}
          size="small"
          aria-label="Notifications"
          sx={{
            color: 'text.primary',
            background: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            borderRadius: '50%',
            width: 40,
            height: 40,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: (t) => `${t.palette.primary.main}18`,
              color: 'primary.main',
            },
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={9}>
            <NotificationsRoundedIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { width: 400, maxHeight: 520, borderRadius: 3, mt: 1 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>Notifications</Typography>
            {unreadCount > 0 && (
              <Chip label={unreadCount} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            {unreadCount > 0 && (
              <Tooltip title="Mark all as read">
                <Button size="small" onClick={handleMarkAllRead} sx={{ minWidth: 0, px: 1, fontWeight: 600, textTransform: 'none', fontSize: '0.75rem' }}>
                  <DoneAllRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} /> Read
                </Button>
              </Tooltip>
            )}
            {visibleNotifications.length > 0 && (
              <Tooltip title="Clear all notifications">
                <Button size="small" color="error" onClick={handleClearAll} sx={{ minWidth: 0, px: 1, fontWeight: 600, textTransform: 'none', fontSize: '0.75rem' }}>
                  <DeleteSweepRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} /> Clear
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Divider />

        {visibleNotifications.length === 0 ? (
          <Box py={4} px={2} textAlign="center">
            <NotificationsRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No notifications</Typography>
            <Typography variant="caption" color="text.disabled">Order updates like placed, packed, shipped will appear here</Typography>
          </Box>
        ) : (
          visibleNotifications.map((notif) => {
            const isRead = readIds.includes(notif.id)
            return (
              <MenuItem
                key={notif.id}
                onClick={() => handleSelect(notif)}
                sx={{
                  py: 1.5,
                  px: 2,
                  alignItems: 'flex-start',
                  gap: 1.5,
                  bgcolor: isRead ? 'transparent' : 'action.hover',
                  borderLeft: isRead ? '3px solid transparent' : '3px solid',
                  borderColor: isRead ? 'transparent' : 'primary.main',
                  pr: 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                  {statusIcon(notif.type, notif.status)}
                </ListItemIcon>
                <Box flex={1} minWidth={0}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Typography variant="body2" fontWeight={isRead ? 500 : 700} sx={{ lineHeight: 1.4 }}>
                      {notif.title}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, whiteSpace: 'nowrap', mt: 0.25 }}>
                      {formatRelativeTime(notif.created_at)}
                    </Typography>
                  </Box>
                  {notif.message && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      {notif.message}
                    </Typography>
                  )}
                  {notif.link && (
                    <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                      View order details →
                    </Typography>
                  )}
                </Box>
                <Tooltip title="Clear">
                  <IconButton
                    size="small"
                    onClick={(e) => handleClearOne(e, notif.id)}
                    sx={{ mt: -0.5, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            )
          })
        )}
      </Menu>
    </>
  )
}

export default NotificationBell
